import { Tabs, Stack, Tab, MenuItem, Menu, IconButton, Skeleton, Chip, ListItemText } from "@mui/material";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import React, { useEffect, useState } from "react";
import { ExpandMore } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import {
  GetActiveTimeline,
  GetFocusedTimelineClip,
  GetTimelineLoading,
  SetActiveTimeline,
  SetCreatingOrEditingTimelineEvent,
  SetFocusedTimelineClip,
  TimelineType,
} from "../../../Redux/Reducers/redux-slice-timeline";
import { useSelector } from "react-redux";
import { PreviewContext } from "../../../Pages/admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";
import { MarkersTab } from "./markers-tab";
import { ClipsTab } from "./clips-tab";
import { StoryboardTab } from "./storyboard-tab";
import { MetadataTab } from "./metadata-tab";
import AccessTab from "./access-tab";
import { TranscriptTab } from "./transcript-tab";
import QuotasTab from "./quotas-tab";
import { useAppSelector } from "../../../Redux/redux-store";

export enum PanelType {
  METADATA = "metadata",
  TIMELINE = "timeline",
  TIMELINE_CLIPS = "clips",
  TIMELINE_MARKERS = "markers",
  TIMELINE_TRANSCRIPT = "transcript",
  TIMELINE_STORYBOARD = "storyboard",
  ACTIVITY = "activity",
  MEDIA_FILES = "mediaFiles",
  ACCESS = "access",
  QUOTAS = "quotas",
}

export interface IPreviewTabProps {
  label: string;
  count?: number;
  value: PanelType;
  children?: IPreviewTabProps[];
}
export interface IPreviewTabsProps {
  tabs: IPreviewTabProps[];
  asset: DbAssetObjectInfos | undefined;
  context?: PreviewContext;
}

export interface IPreviewTabContentProps {
  asset: DbAssetObjectInfos;
  context?: PreviewContext;
}

export const PreviewTabs: React.FC<IPreviewTabsProps> = (props: IPreviewTabsProps) => {
  const { tabs, asset, context = PreviewContext.DistribEnlarged } = props;

  const timelineActive = useAppSelector(GetActiveTimeline);
  const focusedClip = useAppSelector(GetFocusedTimelineClip);

  const [activeTab, setActiveTab] = React.useState<PanelType>(tabs[0].value);
  const [childActiveTab, setChildActiveTab] = React.useState<IPreviewTabProps>();
  const dispatch = useDispatch();

  const [anchorMoreEl, setAnchorMoreEl] = useState<null | HTMLElement>(null);
  const moreOpen = Boolean(anchorMoreEl);

  const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    setAnchorMoreEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setAnchorMoreEl(null);
  };

  const handleChildTabClick = (parentTab: IPreviewTabProps, childTab: IPreviewTabProps) => {
    dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: false }));
    dispatch(SetActiveTimeline(null));
    setActiveTab(parentTab.value);
    setChildActiveTab(childTab);
    handleMoreClose();
  };

  // If there is a focused clip, open it detailed
  useEffect(() => {
    if (focusedClip && activeTab !== PanelType.TIMELINE) {
      const clipsChild = findTimelineChildTab(tabs, PanelType.TIMELINE_CLIPS);
      if (clipsChild !== undefined) {
        setActiveTab(PanelType.TIMELINE);
        setChildActiveTab(clipsChild);
      }
    }
  }, [focusedClip, tabs]);

  // Select according tab when creating / leaving CreatingTimelineEvent panel
  useEffect(() => {
    if (timelineActive) {
      switch (timelineActive) {
        case TimelineType.Markers:
          const markersChild = findTimelineChildTab(tabs, PanelType.TIMELINE_MARKERS);
          if (markersChild !== undefined) {
            setActiveTab(PanelType.TIMELINE);
            setChildActiveTab(markersChild);
          }
          break;
        case TimelineType.Clips:
          const clipsChild = findTimelineChildTab(tabs, PanelType.TIMELINE_CLIPS);
          if (clipsChild !== undefined) {
            setActiveTab(PanelType.TIMELINE);
            setChildActiveTab(clipsChild);
          }
          break;
          break;
      }
    }
  }, [timelineActive]);

  // Reset active timeline when changing tabs to another than timeline
  useEffect(() => {
    if (
      activeTab !== PanelType.TIMELINE &&
      activeTab !== PanelType.TIMELINE_CLIPS &&
      activeTab !== PanelType.TIMELINE_MARKERS &&
      activeTab !== PanelType.TIMELINE_STORYBOARD &&
      activeTab !== PanelType.TIMELINE_TRANSCRIPT
    ) {
      dispatch(SetActiveTimeline(null));
      dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: false }));
    }
  }, [activeTab]);

  return (
    <Stack sx={(theme) => ({ backgroundColor: theme.palette.background.default })} width={"100%"}>
      <Tabs
        value={activeTab}
        variant="fullWidth"
        onChange={(_e, value) => setActiveTab(value)}
        sx={(theme) => ({
          flexShrink: 0,
          position: "sticky",
          top: 0,
          background: theme.palette.background.default,
          zIndex: 2,
        })}
      >
        {tabs.map((tab) => {
          if (tab.children) {
            return [
              <Tab
                key={tab.value}
                label={
                  childActiveTab ? (
                    <>
                      {childActiveTab.label}
                      {childActiveTab.count !== undefined && (
                        <Chip sx={{ ml: 1 }} size="small" variant="filled" color="info" label={childActiveTab.count} />
                      )}
                    </>
                  ) : (
                    tab.label
                  )
                }
                value={tab.value}
                onClick={(e) => childActiveTab === undefined && handleMoreClick(e)}
                icon={
                  <IconButton color="ghost" size="small" onClick={handleMoreClick}>
                    <ExpandMore />
                  </IconButton>
                }
                iconPosition="end"
              />,
              <Menu
                open={moreOpen}
                anchorEl={anchorMoreEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                onClose={handleMoreClose}
              >
                {tab.children.map((child) => (
                  <MenuItem
                    key={child.value}
                    onClick={(e) => handleChildTabClick(tab, child)}
                    disabled={child.value === childActiveTab?.value}
                  >
                    <ListItemText>{child.label}</ListItemText>
                    {child.count !== undefined && (
                      <Chip sx={{ ml: 2 }} size="small" variant="filled" color="info" label={child.count} />
                    )}
                  </MenuItem>
                ))}
              </Menu>,
            ];
          } else return <Tab key={tab.value} label={tab.label} value={tab.value} sx={{ marginRight: 0 }} />;
        })}
      </Tabs>
      {activeTab === PanelType.METADATA && asset !== undefined && <MetadataTab context={context} asset={asset} />}
      {activeTab === PanelType.TIMELINE &&
        childActiveTab?.value === PanelType.TIMELINE_MARKERS &&
        asset !== undefined && (
          <TabLoading>
            <MarkersTab asset={asset} />
          </TabLoading>
        )}
      {activeTab === PanelType.TIMELINE &&
        childActiveTab?.value === PanelType.TIMELINE_CLIPS &&
        asset !== undefined && (
          <TabLoading>
            <ClipsTab asset={asset} />
          </TabLoading>
        )}
      {activeTab === PanelType.TIMELINE &&
        childActiveTab?.value === PanelType.TIMELINE_STORYBOARD &&
        asset !== undefined && (
          <TabLoading>
            <StoryboardTab asset={asset} />
          </TabLoading>
        )}
      {activeTab === PanelType.ACCESS && asset !== undefined && <AccessTab asset={asset} />}
      {activeTab === PanelType.QUOTAS && asset !== undefined && <QuotasTab asset={asset} />}
      {activeTab === PanelType.TIMELINE &&
        childActiveTab?.value === PanelType.TIMELINE_TRANSCRIPT &&
        asset !== undefined && (
          <TabLoading>
            <TranscriptTab asset={asset} />
          </TabLoading>
        )}
    </Stack>
  );
};

function findTimelineChildTab(tabs: IPreviewTabProps[], panelType: PanelType) {
  let activeChild: IPreviewTabProps | undefined = undefined;
  tabs.map((tab) => {
    if (tab.children === undefined) return;
    activeChild = tab.children.find((child) => child.value === panelType);
  });

  return activeChild;
}

const TabLoading = ({ children }) => {
  const isLoading = useSelector(GetTimelineLoading);
  return (
    <>
      {isLoading ? (
        <Stack direction="column" spacing={1} p={2}>
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
        </Stack>
      ) : (
        children
      )}
    </>
  );
};
